import React, { useState } from 'react';
import axios from "axios";
import { notify } from 'helpers/utils';
import {
  Building2, User, Phone, Mail, Banknote, Calendar, Zap,
  FileText, Save, X, Users, CheckCircle, ClipboardList, Clock
} from 'lucide-react';

// ─── DESIGN TOKENS ───────────────────────────────────────────────
const T = {
  bg:         'var(--nd-bg)',
  surface:    'var(--nd-surface)',
  surfaceAlt: 'var(--nd-surface-2)',
  border:     'var(--nd-w06)',
  borderMid:  'var(--nd-w10)',
  text:       'var(--nd-text)',
  textMuted:  'var(--nd-tx35)',
  textFaint:  'var(--nd-w15)',
  accent:     'linear-gradient(135deg,#3B82F6,#22D3A5)',
  error:      '#F87171',
  errorBg:    'rgba(248,113,113,0.08)',
  errorBorder:'rgba(248,113,113,0.35)',
};

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');

  .nv-form * { box-sizing: border-box; }
  .nv-form ::-webkit-scrollbar { width: 4px; }
  .nv-form ::-webkit-scrollbar-track { background: transparent; }
  .nv-form ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }

  .nv-input {
    width: 100%; padding: 11px 14px 11px 44px;
    background: var(--nd-w04);
    border: 1px solid var(--nd-w08);
    border-radius: 10px; color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; transition: border-color 0.15s, background 0.15s;
  }
  .nv-input::placeholder { color: var(--nd-w20); }
  .nv-input:focus { border-color: rgba(96,165,250,0.5); background: rgba(96,165,250,0.04); }
  .nv-input.error { border-color: rgba(248,113,113,0.45); background: rgba(248,113,113,0.06); }

  .nv-input-bare {
    width: 100%; padding: 11px 14px;
    background: var(--nd-w04);
    border: 1px solid var(--nd-w08);
    border-radius: 10px; color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; transition: border-color 0.15s, background 0.15s;
  }
  .nv-input-bare::placeholder { color: var(--nd-w20); }
  .nv-input-bare:focus { border-color: rgba(96,165,250,0.5); background: rgba(96,165,250,0.04); }
  .nv-input-bare.error { border-color: rgba(248,113,113,0.45); background: rgba(248,113,113,0.06); }

  .nv-select {
    width: 100%; padding: 11px 14px;
    background: var(--nd-w04);
    border: 1px solid var(--nd-w08);
    border-radius: 10px; color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='var(--nd-tx30)' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    transition: border-color 0.15s;
  }
  .nv-select option { background: var(--nd-surface-2); color: var(--nd-text); }
  .nv-select:focus { border-color: rgba(96,165,250,0.5); }

  .nv-textarea {
    width: 100%; padding: 11px 14px;
    background: var(--nd-w04);
    border: 1px solid var(--nd-w08);
    border-radius: 10px; color: var(--nd-text);
    font-family: 'DM Sans', sans-serif; font-size: 14px;
    outline: none; resize: vertical; min-height: 90px;
    transition: border-color 0.15s;
  }
  .nv-textarea::placeholder { color: var(--nd-w20); }
  .nv-textarea:focus { border-color: rgba(96,165,250,0.5); background: rgba(96,165,250,0.03); }

  .nv-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 24px; border-radius: 10px; border: none;
    background: linear-gradient(135deg,#3B82F6,#22D3A5);
    color: var(--nd-text-strong); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 700; cursor: pointer;
    transition: opacity 0.15s, transform 0.1s;
  }
  .nv-btn-primary:hover { opacity: 0.88; transform: translateY(-1px); }
  .nv-btn-primary:disabled { background: var(--nd-w10); color: var(--nd-tx30); cursor: not-allowed; transform: none; }

  .nv-btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 22px; border-radius: 10px;
    border: 1px solid var(--nd-w10);
    background: var(--nd-w04);
    color: var(--nd-tx55); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: all 0.15s;
  }
  .nv-btn-ghost:hover { background: var(--nd-w08); color: var(--nd-text); border-color: var(--nd-w20); }

  .nv-btn-danger {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 11px 22px; border-radius: 10px;
    border: 1px solid rgba(248,113,113,0.3);
    background: rgba(248,113,113,0.08);
    color: #F87171; font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: all 0.15s;
  }
  .nv-btn-danger:hover { background: rgba(248,113,113,0.15); }

  .nv-field-icon {
    position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
    color: var(--nd-tx25); pointer-events: none;
  }

  .nv-billing-card {
    position: relative; display: flex; align-items: flex-start;
    padding: 18px; border-radius: 12px; cursor: pointer;
    transition: all 0.18s; border: 1px solid var(--nd-w08);
    background: var(--nd-w02);
  }
  .nv-billing-card:hover { background: var(--nd-w04); border-color: var(--nd-w15); }
  .nv-billing-card.active {
    border-color: rgba(59,130,246,0.5);
    background: rgba(59,130,246,0.08);
    box-shadow: inset 0 0 20px rgba(59,130,246,0.04);
  }

  .nv-step-connector {
    height: 2px; flex: 1; border-radius: 2px; margin-bottom: 28px;
    transition: background 0.4s ease;
  }

  .fade-in { animation: nvFadeIn 0.3s ease; }
  @keyframes nvFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;

// ─── REUSABLE FIELD ───────────────────────────────────────────────
const Field = ({ label, required, error, icon: Icon, children }) => (
  <div>
    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 7, textTransform: 'uppercase' }}>
      {label}{required && <span style={{ color: T.error, marginLeft: 4 }}>*</span>}
    </label>
    <div style={{ position: 'relative' }}>
      {Icon && <Icon size={15} className="nv-field-icon" />}
      {children}
    </div>
    {error && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
        <div style={{ width: 4, height: 4, borderRadius: '50%', background: T.error, flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: T.error }}>{error}</span>
      </div>
    )}
  </div>
);

// ─── SECTION HEADER ───────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, color, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 18, borderBottom: `1px solid ${T.border}` }}>
    <div style={{ width: 38, height: 38, borderRadius: 11, background: color + '18', border: `1px solid ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={18} color={color} />
    </div>
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', lineHeight: 1 }}>{title}</div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{subtitle}</div>
    </div>
  </div>
);

const AddCIClient = () => {
  const [formData, setFormData] = useState({
    client_name: '',
    contact_person: '',
    contact_number: '',
    email: '',
    sales_agent: '',
    contract_type: 'commercial',
    sales_price_before_tax: '',
    total_system_size: '',
    total_panel_count: '',
    total_man_hours: '',
    contract_start_date: '',
    billing_type: 'monthly',
    contractual_services_count: '',
    committed_service_count: '',
    contract_cycles: '',
    jobs_per_cycle: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const validateForm = () => {
    const newErrors = {};
    if (currentStep === 1 || true) {
      if (!formData.client_name.trim()) newErrors.client_name = 'Client name is required';
      if (!formData.contact_person.trim()) newErrors.contact_person = 'Contact person is required';
      if (!formData.contact_number.trim()) {
        newErrors.contact_number = 'Contact number is required';
      } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.contact_number)) {
        newErrors.contact_number = 'Invalid phone number format';
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData.sales_agent.trim()) newErrors.sales_agent = 'Sales agent is required';
    }
    if (currentStep === 2 || true) {
      if (!formData.sales_price_before_tax || formData.sales_price_before_tax <= 0) newErrors.sales_price_before_tax = 'Valid sales price is required';
      if (!formData.total_system_size || formData.total_system_size <= 0) newErrors.total_system_size = 'Valid system size is required';
      if (!formData.total_panel_count || formData.total_panel_count <= 0) newErrors.total_panel_count = 'Valid panel count is required';
      if (!formData.total_man_hours || formData.total_man_hours <= 0) newErrors.total_man_hours = 'Total man hours are required';
      if (!formData.contract_start_date) newErrors.contract_start_date = 'Contract start date is required';
    }
    if (currentStep === 3 || true) {
      if (!formData.committed_service_count || formData.committed_service_count <= 0) newErrors.committed_service_count = 'Committed service count is required';
      if (!formData.contract_cycles || formData.contract_cycles <= 0) newErrors.contract_cycles = 'Number of cycles is required';
      if (!formData.jobs_per_cycle || formData.jobs_per_cycle <= 0) newErrors.jobs_per_cycle = 'Jobs per cycle is required';
      if (formData.billing_type === 'per_service_count' && (!formData.contractual_services_count || formData.contractual_services_count <= 0)) {
        newErrors.contractual_services_count = 'Billing Contractual Services count is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { notify('error', 'Please fix the errors before submitting'); return; }
    setIsSubmitting(true);
    try {
      const dataToSend = {
        client_name: formData.client_name.trim(),
        contact_person: formData.contact_person.trim(),
        contact_number: formData.contact_number.trim(),
        email: formData.email && formData.email.trim() ? formData.email.trim() : null,
        sales_agent: formData.sales_agent.trim(),
        contract_type: formData.contract_type,
        sales_price_before_tax: parseFloat(formData.sales_price_before_tax),
        total_system_size: parseFloat(formData.total_system_size),
        total_panel_count: parseInt(formData.total_panel_count),
        total_man_hours: parseFloat(formData.total_man_hours),
        contract_start_date: formData.contract_start_date,
        billing_type: formData.billing_type,
        committed_service_count: parseInt(formData.committed_service_count),
        contract_cycles: formData.contract_cycles ? parseInt(formData.contract_cycles) : null,
        jobs_per_cycle: formData.jobs_per_cycle ? parseInt(formData.jobs_per_cycle) : null,
        contractual_services_count: formData.billing_type === 'per_service_count' ? parseInt(formData.contractual_services_count) : null,
        notes: formData.notes && formData.notes.trim() ? formData.notes.trim() : null
      };
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/commercial-clients`, dataToSend);
      if (response.data.success) {
        notify('success', `Client "${formData.client_name}" added successfully!`);
        handleReset();
      } else {
        notify('error', 'Error: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error details:', error);
      if (error.response) {
        notify('error', `❌ Error: ${error.response.data.message || 'Failed to create client'}`);
      } else {
        notify('error', `❌ Network Error: Could not reach the server.`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleReset = () => {
    setFormData({
      client_name: '', contact_person: '', contact_number: '', email: '',
      sales_agent: '', contract_type: 'commercial', sales_price_before_tax: '',
      total_system_size: '', total_panel_count: '', total_man_hours: '',
      contract_start_date: '', billing_type: 'monthly',
      contractual_services_count: '', committed_service_count: '',
      contract_cycles: '', jobs_per_cycle: '', notes: ''
    });
    setErrors({});
    setCurrentStep(1);
  };

  const steps = [
    { number: 1, title: 'Client Info',       icon: User     },
    { number: 2, title: 'Contract Details',  icon: FileText },
    { number: 3, title: 'Billing',           icon: Banknote },
  ];

  // ─── RENDER ──────────────────────────────────────────────────────
  return (
    <div className="nv-form" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 20px' }}>
      <style>{GLOBAL_STYLES}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>iNOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Add C&I Client</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Commercial & Industrial Solar Projects</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
      </div>

      {/* ── STEP INDICATOR ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 28px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {steps.map((step, idx) => {
            const done   = currentStep > step.number;
            const active = currentStep === step.number;
            return (
              <React.Fragment key={step.number}>
                {/* Step circle */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div
                    onClick={() => step.number < currentStep && setCurrentStep(step.number)}
                    style={{
                      width: 40, height: 40, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: done || active ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'var(--nd-w05)',
                      border: done || active ? 'none' : `1px solid var(--nd-w10)`,
                      color: done || active ? 'var(--nd-bg)' : T.textMuted,
                      fontWeight: 800, fontSize: 14,
                      boxShadow: active ? '0 0 0 4px rgba(59,130,246,0.2)' : 'none',
                      cursor: done ? 'pointer' : 'default',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {done ? <CheckCircle size={18} /> : <step.icon size={16} />}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: active ? '#60A5FA' : done ? '#22D3A5' : T.textFaint }}>
                    {step.title.toUpperCase()}
                  </div>
                </div>

                {/* Connector */}
                {idx < 2 && (
                  <div className="nv-step-connector" style={{ background: currentStep > step.number ? 'linear-gradient(90deg,#3B82F6,#22D3A5)' : 'var(--nd-w08)', margin: '0 12px 28px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── FORM CARD ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>

        {/* ══ STEP 1: Client Information ══ */}
        {currentStep === 1 && (
          <div className="fade-in" style={{ padding: '28px 32px' }}>
            <SectionHeader icon={User} color="#60A5FA" title="Client Information" subtitle="Basic details about the commercial client" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Client Name */}
              <Field label="Client Name" required error={errors.client_name} icon={Building2}>
                <input type="text" name="client_name" value={formData.client_name} onChange={handleChange}
                  className={`nv-input${errors.client_name ? ' error' : ''}`} placeholder="e.g., Nizam Industries" />
              </Field>

              {/* 2-col row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
                <Field label="Contact Person" required error={errors.contact_person} icon={User}>
                  <input type="text" name="contact_person" value={formData.contact_person} onChange={handleChange}
                    className={`nv-input${errors.contact_person ? ' error' : ''}`} placeholder="e.g., Ahmed Khan" />
                </Field>
                <Field label="Contact Number" required error={errors.contact_number} icon={Phone}>
                  <input type="tel" name="contact_number" value={formData.contact_number} onChange={handleChange}
                    className={`nv-input${errors.contact_number ? ' error' : ''}`} placeholder="0300-1234567" />
                </Field>
                <Field label="Email Address" error={errors.email} icon={Mail}>
                  <input type="email" name="email" value={formData.email} onChange={handleChange}
                    className={`nv-input${errors.email ? ' error' : ''}`} placeholder="client@example.com" />
                </Field>
                <Field label="Sales Agent" required error={errors.sales_agent} icon={Users}>
                  <input type="text" name="sales_agent" value={formData.sales_agent} onChange={handleChange}
                    className={`nv-input${errors.sales_agent ? ' error' : ''}`} placeholder="e.g., Ali Raza" />
                </Field>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
              <button className="nv-btn-primary" onClick={() => setCurrentStep(2)}>
                Continue to Contract Details →
              </button>
            </div>
          </div>
        )}

        {/* ══ STEP 2: Contract Details ══ */}
        {currentStep === 2 && (
          <div className="fade-in" style={{ padding: '28px 32px' }}>
            <SectionHeader icon={FileText} color="#A78BFA" title="Contract Details" subtitle="Pricing and contract information" />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
              <Field label="Contract Type" required>
                <select name="contract_type" value={formData.contract_type} onChange={handleChange} className="nv-select">
                  <option value="commercial">Commercial</option>
                  <option value="industrial">Industrial</option>
                </select>
              </Field>

              <Field label="Contract Start Date" required error={errors.contract_start_date} icon={Calendar}>
                <input type="date" name="contract_start_date" value={formData.contract_start_date} onChange={handleChange}
                  className={`nv-input${errors.contract_start_date ? ' error' : ''}`} style={{ colorScheme: 'dark' }} />
              </Field>

              <Field label="Sales Price Before Tax (PKR)" required error={errors.sales_price_before_tax} icon={Banknote}>
                <input type="number" name="sales_price_before_tax" value={formData.sales_price_before_tax} onChange={handleChange}
                  className={`nv-input${errors.sales_price_before_tax ? ' error' : ''}`} placeholder="50000" min="0" />
              </Field>

              <Field label="Total System Size (kW)" required error={errors.total_system_size} icon={Zap}>
                <input type="number" name="total_system_size" value={formData.total_system_size} onChange={handleChange}
                  className={`nv-input${errors.total_system_size ? ' error' : ''}`} placeholder="5000" min="0" />
              </Field>

              <Field label="Total Panel Count" required error={errors.total_panel_count} icon={Zap}>
                <input type="number" name="total_panel_count" value={formData.total_panel_count} onChange={handleChange}
                  className={`nv-input${errors.total_panel_count ? ' error' : ''}`} placeholder="5000" min="0" />
              </Field>

              <Field label="Total Man Hours Needed" required error={errors.total_man_hours} icon={Clock}>
                <input type="number" name="total_man_hours" value={formData.total_man_hours} onChange={handleChange}
                  className={`nv-input${errors.total_man_hours ? ' error' : ''}`} placeholder="e.g., 160" step="0.1" min="0" />
              </Field>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
              <button className="nv-btn-ghost" onClick={() => setCurrentStep(1)}>← Back</button>
              <button className="nv-btn-primary" onClick={() => setCurrentStep(3)}>Continue to Billing →</button>
            </div>
          </div>
        )}

        {/* ══ STEP 3: Billing Information ══ */}
        {currentStep === 3 && (
          <div className="fade-in" style={{ padding: '28px 32px' }}>
            <SectionHeader icon={Banknote} color="#22D3A5" title="Billing Information" subtitle="Configure billing preferences" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Billing Type */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: '0.14em', marginBottom: 12, textTransform: 'uppercase' }}>
                  Billing Type <span style={{ color: T.error }}>*</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>

                  {/* Monthly */}
                  <label className={`nv-billing-card${formData.billing_type === 'monthly' ? ' active' : ''}`}>
                    <input type="radio" name="billing_type" value="monthly" checked={formData.billing_type === 'monthly'} onChange={handleChange}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: formData.billing_type === 'monthly' ? 'rgba(59,130,246,0.2)' : 'var(--nd-w05)', border: `1px solid ${formData.billing_type === 'monthly' ? 'rgba(59,130,246,0.5)' : 'var(--nd-w08)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 14, transition: 'all 0.18s' }}>
                      <Calendar size={16} color={formData.billing_type === 'monthly' ? '#60A5FA' : T.textMuted} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: formData.billing_type === 'monthly' ? T.text : T.textMuted, marginBottom: 4 }}>Monthly Billing</div>
                      <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.5 }}>Bill generated at end of every month</div>
                    </div>
                    {formData.billing_type === 'monthly' && (
                      <CheckCircle size={16} color="#22D3A5" style={{ flexShrink: 0, marginLeft: 8 }} />
                    )}
                  </label>

                  {/* Per Service Count */}
                  <label className={`nv-billing-card${formData.billing_type === 'per_service_count' ? ' active' : ''}`}>
                    <input type="radio" name="billing_type" value="per_service_count" checked={formData.billing_type === 'per_service_count'} onChange={handleChange}
                      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: formData.billing_type === 'per_service_count' ? 'rgba(59,130,246,0.2)' : 'var(--nd-w05)', border: `1px solid ${formData.billing_type === 'per_service_count' ? 'rgba(59,130,246,0.5)' : 'var(--nd-w08)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 14, transition: 'all 0.18s' }}>
                      <ClipboardList size={16} color={formData.billing_type === 'per_service_count' ? '#60A5FA' : T.textMuted} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: formData.billing_type === 'per_service_count' ? T.text : T.textMuted, marginBottom: 4 }}>Per Contractual Services</div>
                      <div style={{ fontSize: 11, color: T.textFaint, lineHeight: 1.5 }}>Bill generated after X number of washes</div>
                    </div>
                    {formData.billing_type === 'per_service_count' && (
                      <CheckCircle size={16} color="#22D3A5" style={{ flexShrink: 0, marginLeft: 8 }} />
                    )}
                  </label>
                </div>
              </div>

              {/* Committed Service Count */}
              <div style={{ background: 'var(--nd-w02)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 20px' }}>
                <Field label="Committed Washes (Total)" required error={errors.committed_service_count} icon={ClipboardList}>
                  <input type="number" name="committed_service_count" value={formData.committed_service_count} onChange={handleChange}
                    className={`nv-input${errors.committed_service_count ? ' error' : ''}`} placeholder="e.g., 4" min="1" />
                </Field>
              </div>

              {/* Cycle configuration (drives billing-ready) */}
              <div style={{ background: 'rgba(34,211,165,0.05)', border: '1px solid rgba(34,211,165,0.22)', borderRadius: 12, padding: '18px 20px' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#22D3A5', letterSpacing: '0.16em', marginBottom: 12 }}>
                  CONTRACT CYCLES (FOR BILLING)
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
                  <Field label="Number of Cycles" required error={errors.contract_cycles} icon={ClipboardList}>
                    <input type="number" name="contract_cycles" value={formData.contract_cycles} onChange={handleChange}
                      className={`nv-input${errors.contract_cycles ? ' error' : ''}`} placeholder="e.g., 12" min="1" />
                  </Field>
                  <Field label="Jobs per Cycle" required error={errors.jobs_per_cycle} icon={ClipboardList}>
                    <input type="number" name="jobs_per_cycle" value={formData.jobs_per_cycle} onChange={handleChange}
                      className={`nv-input${errors.jobs_per_cycle ? ' error' : ''}`} placeholder="e.g., 4" min="1" />
                  </Field>
                </div>
                <div style={{ fontSize: 11, color: T.textFaint, marginTop: 10, lineHeight: 1.5 }}>
                  A cycle completes after this many completed jobs. When all cycles are done, the client is flagged ready for billing.
                </div>
              </div>

              {/* Conditional: Contractual Services Count */}
              {formData.billing_type === 'per_service_count' && (
                <div className="fade-in" style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 12, padding: '18px 20px' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#60A5FA', letterSpacing: '0.16em', marginBottom: 12 }}>
                    BILLING THRESHOLD
                  </div>
                  <Field label="Contractual Services Count" required error={errors.contractual_services_count}>
                    <input type="number" name="contractual_services_count" value={formData.contractual_services_count} onChange={handleChange}
                      className={`nv-input-bare${errors.contractual_services_count ? ' error' : ''}`} placeholder="e.g., 2" min="1" />
                  </Field>
                </div>
              )}

              {/* Notes */}
              <Field label="Additional Notes">
                <textarea name="notes" value={formData.notes} onChange={handleChange}
                  className="nv-textarea" placeholder="Any additional information…" />
              </Field>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
              <button className="nv-btn-ghost" onClick={() => setCurrentStep(2)}>← Back</button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="nv-btn-danger" onClick={handleReset}>
                  <X size={15} /> Reset
                </button>
                <button className="nv-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
                  <Save size={15} />
                  {isSubmitting ? 'Saving…' : 'Save Client'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCIClient;